import type { CampaignStatistics } from '@hprofits/api';
import type { AdGroupListView } from '@hprofits/api/ad-group/types';
import type { CreativeGroupListItem } from '@hprofits/api/creative-group/types';
import type { TargetGroupListView } from '@hprofits/api/target-group/types';
import { useDeleteAdGroup, useDeleteCreativeGroup, useDeleteTargetGroup } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import type { AdGroupId, CampaignGroupId, CreativeGroupId, TargetGroupId } from '@hprofits/shared';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, ref, toValue } from 'vue';
import type { RouteLocation } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';

import { apiClient } from '@/api-client';
import { t } from '@/i18n';
import { isEntityDeleteLockedByPricingRule } from '@/modules/campaigns/composables/usePricingRuleLock';
import {
  getAdGroupDeleteGenericErrorMessage,
  getAdGroupDeleteMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/adGroup';
import {
  getCreativeGroupDeleteGenericErrorMessage,
  getCreativeGroupDeleteMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/creativeGroup';
import { formatMutationError } from '@/modules/campaigns/utils/mutation-errors/shared';
import {
  getTargetGroupDeleteGenericErrorMessage,
  getTargetGroupDeleteMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/targetGroup';
import { pickFallbackRedirectId } from '@/modules/campaigns/utils/pickFallbackRedirectId';
import { useEntityDeleteDialog } from '@/modules/common/composables/useEntityDeleteDialog';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';

type DeleteSource<TId> = Readonly<{ id: TId; name: string }>;

export type TargetGroupDeleteSource = Pick<TargetGroupListView, 'id' | 'name'>;
export type CreativeGroupDeleteSource = Pick<CreativeGroupListItem, 'id' | 'name'>;
export type AdGroupDeleteSource = Pick<AdGroupListView, 'id' | 'name'>;

type DeleteVars<TId> = Readonly<{
  id: TId;
  campaignGroupId: CampaignGroupId;
  redirectLocation: RouteLocation | null;
}>;

type UseCampaignEntityDeleteActionsProps = Readonly<{
  campaignGroupId: CampaignGroupId;
  targetGroups: MaybeRefOrGetter<ReadonlyArray<TargetGroupDeleteSource>>;
  creativeGroups: MaybeRefOrGetter<ReadonlyArray<CreativeGroupDeleteSource>>;
  adGroups: MaybeRefOrGetter<ReadonlyArray<AdGroupDeleteSource>>;
  campaignStatistics: MaybeRefOrGetter<CampaignStatistics | null | undefined>;
  pageController: UsePageControllerResult;
  allowNextNavigation: () => void;
}>;

export type UseCampaignEntityDeleteActionsResult = Readonly<{
  isPending: Readonly<Ref<boolean>>;
  deletingTargetGroupId: Readonly<Ref<TargetGroupId | null>>;
  deletingCreativeGroupId: Readonly<Ref<CreativeGroupId | null>>;
  deletingAdGroupId: Readonly<Ref<AdGroupId | null>>;
  isTargetGroupLocked: (id: TargetGroupId) => boolean;
  isCreativeGroupLocked: (id: CreativeGroupId) => boolean;
  deleteTargetGroup: (source: TargetGroupDeleteSource) => Promise<void>;
  deleteCreativeGroup: (source: CreativeGroupDeleteSource) => Promise<void>;
  deleteAdGroup: (source: AdGroupDeleteSource) => Promise<void>;
}>;

export type CampaignEntityDeletionState = Omit<
  UseCampaignEntityDeleteActionsResult,
  'deleteTargetGroup' | 'deleteCreativeGroup' | 'deleteAdGroup'
>;

const isSameRouteId = (routeValue: unknown, id: number): boolean => String(routeValue ?? '') === String(id);

export const useCampaignEntityDeleteActions = ({
  campaignGroupId,
  targetGroups,
  creativeGroups,
  adGroups,
  campaignStatistics,
  pageController,
  allowNextNavigation,
}: UseCampaignEntityDeleteActionsProps): UseCampaignEntityDeleteActionsResult => {
  const route = useRoute();
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();

  const targetMutation = useDeleteTargetGroup(apiClient, SUPPRESS_ERROR_TOAST_META);
  const creativeMutation = useDeleteCreativeGroup(apiClient, SUPPRESS_ERROR_TOAST_META);
  const adMutation = useDeleteAdGroup(apiClient, SUPPRESS_ERROR_TOAST_META);

  const targetCandidate = ref<TargetGroupDeleteSource | null>(null);
  const creativeCandidate = ref<CreativeGroupDeleteSource | null>(null);
  const adCandidate = ref<AdGroupDeleteSource | null>(null);

  const resolveRedirect = <TId extends number, TSource extends DeleteSource<TId>>({
    source,
    list,
    routeParam,
    resolveSibling,
  }: {
    source: TSource;
    list: ReadonlyArray<TSource>;
    routeParam: unknown;
    resolveSibling: (id: TId) => RouteLocation;
  }): RouteLocation | null => {
    if (!isSameRouteId(routeParam, source.id)) {
      return null;
    }

    const siblingId = pickFallbackRedirectId(list, source.id);
    return siblingId === null ? routeResolver.summary(campaignGroupId) : resolveSibling(siblingId);
  };

  const isTargetGroupLocked = (id: TargetGroupId): boolean =>
    isEntityDeleteLockedByPricingRule(toValue(campaignStatistics), 'target', id);
  const isCreativeGroupLocked = (id: CreativeGroupId): boolean =>
    isEntityDeleteLockedByPricingRule(toValue(campaignStatistics), 'creative', id);

  const targetDialog = useEntityDeleteDialog<DeleteVars<TargetGroupId>, void, TargetGroupDeleteSource>({
    resolveDisplayName: (source) => source.name,
    confirmCopy: {
      title: () => t('modules.campaigns.actions.deleteTargetGroup'),
      description: (displayName) => t('modules.campaigns.actions.deleteTargetGroupDescription', { name: displayName }),
      confirmText: () => t('modules.campaigns.actions.delete'),
    },
    buildVars: (source) => ({
      id: source.id,
      campaignGroupId,
      redirectLocation: resolveRedirect<TargetGroupId, TargetGroupDeleteSource>({
        source,
        list: toValue(targetGroups),
        routeParam: route.params.targetId,
        resolveSibling: (id) => routeResolver.targetGroup(campaignGroupId, id),
      }),
    }),
    mutate: (vars) => targetMutation.mutateAsync({ targetGroupId: vars.id, campaignId: vars.campaignGroupId }),
    isPending: targetMutation.isPending,
    buildErrorMessage: (err) =>
      formatMutationError(err, getTargetGroupDeleteMutationErrorOverride, getTargetGroupDeleteGenericErrorMessage()),
    successMessage: () => t('modules.campaigns.actions.targetGroupDeletedToast'),
    onSuccess: async (_data, vars) => {
      if (vars.redirectLocation) {
        allowNextNavigation();
        await router.push(vars.redirectLocation);
      }
    },
    pageController,
  });

  const creativeDialog = useEntityDeleteDialog<DeleteVars<CreativeGroupId>, void, CreativeGroupDeleteSource>({
    resolveDisplayName: (source) => source.name,
    confirmCopy: {
      title: () => t('modules.campaigns.actions.deleteCreativeGroup'),
      description: (displayName) =>
        t('modules.campaigns.actions.deleteCreativeGroupDescription', { name: displayName }),
      confirmText: () => t('modules.campaigns.actions.delete'),
    },
    buildVars: (source) => ({
      id: source.id,
      campaignGroupId,
      redirectLocation: resolveRedirect<CreativeGroupId, CreativeGroupDeleteSource>({
        source,
        list: toValue(creativeGroups),
        routeParam: route.params.creativeId,
        resolveSibling: (id) => routeResolver.creativeGroup(campaignGroupId, id),
      }),
    }),
    mutate: (vars) => creativeMutation.mutateAsync({ creativeGroupId: vars.id, campaignId: vars.campaignGroupId }),
    isPending: creativeMutation.isPending,
    buildErrorMessage: (err) =>
      formatMutationError(
        err,
        getCreativeGroupDeleteMutationErrorOverride,
        getCreativeGroupDeleteGenericErrorMessage(),
      ),
    successMessage: () => t('modules.campaigns.actions.creativeGroupDeletedToast'),
    onSuccess: async (_data, vars) => {
      if (vars.redirectLocation) {
        allowNextNavigation();
        await router.push(vars.redirectLocation);
      }
    },
    pageController,
  });

  const adDialog = useEntityDeleteDialog<DeleteVars<AdGroupId>, void, AdGroupDeleteSource>({
    resolveDisplayName: (source) => source.name,
    confirmCopy: {
      title: () => t('modules.campaigns.actions.deleteAdGroup'),
      description: (displayName) => t('modules.campaigns.actions.deleteAdGroupDescription', { name: displayName }),
      confirmText: () => t('modules.campaigns.actions.delete'),
    },
    buildVars: (source) => ({
      id: source.id,
      campaignGroupId,
      redirectLocation: resolveRedirect<AdGroupId, AdGroupDeleteSource>({
        source,
        list: toValue(adGroups),
        routeParam: route.params.adId,
        resolveSibling: (id) => routeResolver.adGroup(campaignGroupId, id),
      }),
    }),
    mutate: (vars) => adMutation.mutateAsync({ adGroupId: vars.id, campaignId: vars.campaignGroupId }),
    isPending: adMutation.isPending,
    buildErrorMessage: (err) =>
      formatMutationError(err, getAdGroupDeleteMutationErrorOverride, getAdGroupDeleteGenericErrorMessage()),
    successMessage: () => t('modules.campaigns.actions.adGroupDeletedToast'),
    onSuccess: async (_data, vars) => {
      if (vars.redirectLocation) {
        allowNextNavigation();
        await router.push(vars.redirectLocation);
      }
    },
    pageController,
  });

  const deleteTargetGroup = async (source: TargetGroupDeleteSource): Promise<void> => {
    if (targetCandidate.value || isTargetGroupLocked(source.id)) {
      return;
    }
    targetCandidate.value = source;
    try {
      await targetDialog.openDeleteDialog(source);
    } finally {
      targetCandidate.value = null;
    }
  };

  const deleteCreativeGroup = async (source: CreativeGroupDeleteSource): Promise<void> => {
    if (creativeCandidate.value || isCreativeGroupLocked(source.id)) {
      return;
    }
    creativeCandidate.value = source;
    try {
      await creativeDialog.openDeleteDialog(source);
    } finally {
      creativeCandidate.value = null;
    }
  };

  const deleteAdGroup = async (source: AdGroupDeleteSource): Promise<void> => {
    if (adCandidate.value) {
      return;
    }
    adCandidate.value = source;
    try {
      await adDialog.openDeleteDialog(source);
    } finally {
      adCandidate.value = null;
    }
  };

  return {
    isPending: computed(
      () => targetMutation.isPending.value || creativeMutation.isPending.value || adMutation.isPending.value,
    ),
    deletingTargetGroupId: computed(() =>
      targetMutation.isPending.value ? (targetCandidate.value?.id ?? null) : null,
    ),
    deletingCreativeGroupId: computed(() =>
      creativeMutation.isPending.value ? (creativeCandidate.value?.id ?? null) : null,
    ),
    deletingAdGroupId: computed(() => (adMutation.isPending.value ? (adCandidate.value?.id ?? null) : null)),
    isTargetGroupLocked,
    isCreativeGroupLocked,
    deleteTargetGroup,
    deleteCreativeGroup,
    deleteAdGroup,
  };
};
